import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Logo } from '../components/ui';
import { colors, type, radius, btn, card as cardStyle, heading } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export default function DropPage() {
  const { token } = useParams();
  const [linkInfo, setLinkInfo] = useState(null); // null=loading, false=invalid
  const [uploads, setUploads] = useState([]); // [{name, status, error}]
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);

  // Download state
  const [downloadFiles, setDownloadFiles] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Validate the share link
  useEffect(() => {
    async function validate() {
      try {
        const res = await fetch(`${API_URL}/api/drop/${token}`);
        if (!res.ok) { setLinkInfo(false); return; }
        const data = await res.json();
        setLinkInfo(data);
      } catch {
        setLinkInfo(false);
      }
    }
    validate();
  }, [token]);

  // Fetch download files when link supports download
  useEffect(() => {
    if (!linkInfo || linkInfo.mode === 'upload') return;
    async function fetchFiles() {
      setDownloadLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/drop/${token}/download`);
        if (!res.ok) return;
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          if (data.type === 'directory') {
            setDownloadFiles(data.files || []);
          } else {
            // Single file — no file list needed
            setDownloadFiles([]);
          }
        }
      } catch {
        // ignore
      } finally {
        setDownloadLoading(false);
      }
    }
    fetchFiles();
  }, [linkInfo, token]);

  const handleFiles = useCallback(async (files) => {
    const fileList = Array.from(files);
    for (const file of fileList) {
      // Add a random component so two same-name files dropped in the same
      // millisecond don't produce duplicate React keys.
      const id = Date.now() + '_' + Math.random().toString(36).slice(2) + '_' + file.name;
      setUploads(prev => [...prev, { id, name: file.name, status: 'uploading' }]);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API_URL}/api/drop/${token}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Upload failed');
        }

        setUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'done' } : u));
      } catch (err) {
        setUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'error', error: err.message } : u));
      }
    }
  }, [token]);

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }

  function handleDownloadFile(fileName) {
    const url = `${API_URL}/api/drop/${token}/download?file=${encodeURIComponent(fileName)}`;
    window.open(url, '_blank');
  }

  function handleDownloadSingle() {
    const url = `${API_URL}/api/drop/${token}/download`;
    window.open(url, '_blank');
  }

  // ─── Invalid link ───
  if (linkInfo === false) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.iconWrap}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1 style={s.title}>Link expired or invalid</h1>
          <p style={s.subtitle}>This link is no longer active. Ask the sender for a new one.</p>
        </div>
      </div>
    );
  }

  // ─── Loading ───
  if (linkInfo === null) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={{ color: colors.textMuted, fontSize: '14px' }}>Checking link...</div>
        </div>
      </div>
    );
  }

  // ─── Main content ───
  const showUpload = linkInfo.mode === 'upload' || linkInfo.mode === 'both';
  const showDownload = linkInfo.mode === 'download' || linkInfo.mode === 'both';
  const isDirectory = linkInfo.target_type === 'directory';
  const doneCount = uploads.filter(u => u.status === 'done').length;
  const allDone = uploads.length > 0 && uploads.every(u => u.status === 'done' || u.status === 'error');

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoRow}>
          <Logo variant="horizontal" size={22} />
        </div>

        <h1 style={s.title}>
          {showUpload && showDownload ? 'Shared files' : showDownload ? 'Download files' : 'Upload files'}
        </h1>
        {linkInfo.created_by_email && (
          <p style={s.subtitle}>Shared by {linkInfo.created_by_email}</p>
        )}

        {/* Download section */}
        {showDownload && (
          <div style={{ marginBottom: showUpload ? '24px' : '0' }}>
            {downloadLoading ? (
              <div style={{ color: colors.textMuted, fontSize: '13px', padding: '16px 0' }}>Loading files...</div>
            ) : isDirectory && downloadFiles.length > 0 ? (
              <div style={s.fileList}>
                {downloadFiles.map((f, i) => (
                  <div key={i} style={s.fileRow}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke={colors.textMuted} strokeWidth="1.5" style={{ flexShrink: 0 }}>
                      <path d="M4 2h8l4 4v10a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zM12 2v4h4" />
                    </svg>
                    <span style={s.fileRowName}>{f.name}</span>
                    <button onClick={() => handleDownloadFile(f.name)} className="mc-ghost" style={s.dlBtn}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 2v9M4 8l4 4 4-4M2 13h12" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : isDirectory && downloadFiles.length === 0 && !downloadLoading ? (
              <div style={{ color: colors.textFaint, fontSize: '13px', padding: '16px 0' }}>No files available for download.</div>
            ) : (
              <button onClick={handleDownloadSingle} className="mc-btn" style={s.downloadBtn}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 2v9M4 8l4 4 4-4M2 13h12" /></svg>
                Download File
              </button>
            )}
          </div>
        )}

        {/* Divider between download and upload */}
        {showDownload && showUpload && (
          <div style={{ borderTop: `1px solid ${colors.border}`, marginBottom: '20px' }} />
        )}

        {/* Upload section */}
        {showUpload && (
          <>
            {showDownload && <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text, marginBottom: '12px' }}>Upload files</div>}
            <div
              style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}) }}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => document.getElementById('drop-input').click()}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={dragOver ? colors.accent : colors.textFaint} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div style={{ fontSize: '15px', fontWeight: 500, color: dragOver ? colors.accentText : colors.textSecondary, marginTop: '12px' }}>
                {dragOver ? 'Drop to upload' : 'Drag files here or click to browse'}
              </div>
              <input
                id="drop-input"
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files.length) handleFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {/* Upload list */}
            {uploads.length > 0 && (
              <div style={s.uploadList}>
                {uploads.map(u => (
                  <div key={u.id} style={s.uploadRow}>
                    <span style={s.uploadName}>{u.name}</span>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: u.status === 'done' ? colors.success : u.status === 'error' ? colors.danger : colors.syncText }}>
                      {u.status === 'uploading' ? 'Uploading...' : u.status === 'done' ? 'Synced' : u.error || 'Error'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {allDone && doneCount > 0 && (
              <div style={s.successMsg}>
                {doneCount} file{doneCount !== 1 ? 's' : ''} uploaded successfully.
              </div>
            )}
          </>
        )}

        {error && <div style={s.errorMsg}>{error}</div>}

        {linkInfo.expires_at && (
          <div style={s.expiryNote}>
            Link expires {new Date(linkInfo.expires_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '24px 16px',
    background: colors.bg,
  },
  card: {
    width: '100%',
    maxWidth: '480px',
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.xl,
    padding: '28px',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '22px',
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  title: {
    ...heading.display,
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: type.md,
    color: colors.textMuted,
    margin: '0 0 20px',
  },
  dropZone: {
    border: `1px dashed ${colors.borderStrong}`,
    borderRadius: radius.lg,
    padding: '36px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    background: colors.inputBg,
    transition: 'border-color 120ms ease, background-color 120ms ease',
  },
  dropZoneActive: {
    borderColor: colors.accentBorder,
    background: colors.accentSoft,
  },
  uploadList: {
    ...cardStyle,
    marginTop: '12px',
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  uploadRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '6px 8px',
    borderRadius: radius.sm,
  },
  uploadName: {
    fontSize: type.md,
    color: colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '70%',
  },
  successMsg: {
    marginTop: '12px',
    padding: '10px 12px',
    borderRadius: radius.md,
    background: colors.accentSoft,
    border: `1px solid ${colors.accentBorder}`,
    color: colors.accentText,
    fontSize: type.md,
    textAlign: 'center',
    fontWeight: 500,
  },
  errorMsg: {
    marginTop: '12px',
    padding: '10px 12px',
    borderRadius: radius.md,
    background: colors.dangerSoft,
    border: `1px solid ${colors.dangerBorder}`,
    color: colors.danger,
    fontSize: type.md,
  },
  expiryNote: {
    marginTop: '16px',
    fontSize: type.sm,
    color: colors.textFaint,
    textAlign: 'center',
  },
  // Download styles
  fileList: {
    ...cardStyle,
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '4px 4px 4px 8px',
    borderRadius: radius.sm,
  },
  fileRowName: {
    flex: 1,
    fontSize: type.md,
    color: colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dlBtn: {
    ...btn.base,
    ...btn.ghost,
    ...btn.iconSm,
  },
  downloadBtn: {
    ...btn.base,
    ...btn.primary,
    ...btn.lg,
  },
};
